import React, { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import MuiAppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import { styled, useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { useLocation } from "react-router-dom";
import { AdminLanguageProvider } from "../../context/AdminLanguageContext";
import { clearMonetagPageAds } from "../../pages/components/MonetagAds";
import AdminNavigationBar from "./AdminNavigationBar";
import AdminSideBar from "./AdminSideBar";
import AdminTopBar from "./AdminTopBar";

const drawerWidth = 240;

function HideOnScroll(props) {
    const { children, window } = props;
    const trigger = useScrollTrigger({
        target: window ? window() : undefined,
    });

    return (
        <Slide appear={false} direction="down" in={!trigger}>
            {children}
        </Slide>
    );
}

const drawerPaperColumn = {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    height: "100vh",
    maxHeight: "100vh",
    boxSizing: "border-box",
    overflow: "hidden",
};

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    ...drawerPaperColumn,
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...drawerPaperColumn,
    width: 0,
    minWidth: 0,
    overflow: "hidden",
    visibility: "hidden",
    pointerEvents: "none",
    borderRight: "none",
});

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    flexShrink: 0,
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up("md")]: {
        ...(open && {
            marginLeft: drawerWidth,
            width: `calc(100% - ${drawerWidth}px)`,
            transition: theme.transitions.create(["width", "margin"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    },
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    height: "100vh",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));

const Layout = ({ children }) => {
    const location = useLocation();
    const theme = useTheme();
    const monetagTest = location.pathname.startsWith("/admin/system/anything/test-monetag");

    useEffect(() => {
        if (monetagTest) {
            document.documentElement.classList.remove("pi-admin-no-banner");
            return undefined;
        }
        document.documentElement.classList.add("pi-admin-no-banner");
        clearMonetagPageAds();
        return () => document.documentElement.classList.remove("pi-admin-no-banner");
    }, [monetagTest]);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [open, setOpen] = useState(false);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const closeChevron = theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />;

    const drawerInner = (
        <Box
            sx={{
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}
        >
            {open && (
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose} aria-label="close sidebar">
                        {closeChevron}
                    </IconButton>
                </DrawerHeader>
            )}
            <Divider sx={{ flexShrink: 0 }} />
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "scroll",
                    overflowX: "hidden",
                    pb: 10,
                }}
            >
                <AdminSideBar drawerOpen={open} setDrawerOpen={setOpen} />
            </Box>
            {open && (
                <IconButton
                    onClick={handleDrawerClose}
                    aria-label="close sidebar"
                    sx={{
                        position: "absolute",
                        right: 0,
                        bottom: 4,
                        zIndex: 3,
                        width: 56,
                        height: 56,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "grey.100" },
                        "& .MuiSvgIcon-root": { fontSize: 40 },
                    }}
                >
                    {closeChevron}
                </IconButton>
            )}
        </Box>
    );

    return (
        <AdminLanguageProvider>
            <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
                <CssBaseline />
                <HideOnScroll>
                    <AppBar position="fixed" open={open} sx={{ backgroundColor: "#3BA666", pt: { xs: "max(env(safe-area-inset-top), 24px)", md: 0 } }}>
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerOpen}
                                edge="start"
                                sx={{
                                    marginRight: 5,
                                    ...(open && !isMobile && { display: "none" }),
                                }}
                            >
                                <MenuIcon />
                            </IconButton>

                            {/* Top bar */}
                            <AdminTopBar open={open} handleDrawerOpen={handleDrawerOpen} />
                        </Toolbar>
                    </AppBar>
                </HideOnScroll>
                {isMobile ? (
                    <MuiDrawer
                        variant="temporary"
                        open={open}
                        onClose={handleDrawerClose}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                        sx={{
                            display: { xs: "block", md: "none" },
                            "& .MuiDrawer-paper": {
                                boxSizing: "border-box",
                                width: drawerWidth,
                                ...drawerPaperColumn,
                            },
                        }}
                    >
                        {drawerInner}
                    </MuiDrawer>
                ) : (
                    <Drawer variant="permanent" open={open}>
                        {drawerInner}
                    </Drawer>
                )}
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        height: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <DrawerHeader />

                    <AdminNavigationBar />
                    {/* Page — minHeight:0 + overflow so tall admin pages can scroll */}
                    <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
                        {children}
                    </Box>
                </Box>
            </Box>
        </AdminLanguageProvider>
    );
};

export default Layout;
